import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type ControlPlaneAuthType = 'None' | 'HeaderSecret' | 'BearerToken' | 'CustomHeaders';
type InstallAckMode = 'Sync' | 'Async';
type SupportedEventPattern = string;
type IntegrationAppStatus = 'Draft' | 'InReview' | 'Rejected' | 'Active' | 'Disabled' | 'Deleted';
type ReviewStatus = 'Draft' | 'InReview' | 'Rejected';
export type OfficialThirdPartyTabType = 'connected' | 'drafts' | 'review';
type RejectReasonCode =
  | 'DeveloperInfoMissing'
  | 'PrivacyPolicyMissing'
  | 'InstallEndpointInvalid'
  | 'PermissionScopeIncomplete'
  | 'StoreContentInvalid'
  | 'Other';

interface AppReviewItem {
  id: string;
  appName: string;
  appId: string;
  provider: string;
  channel: string;
  version: string;
  submittedAt: string;
  status: ReviewStatus;
  description: string;
  category?: string;
  shortDescription?: string;
  iconUrl?: string;
  controlPlaneBaseUrl: string;
  controlPlaneAuthType: ControlPlaneAuthType;
  controlPlaneSecret?: string;
  controlPlaneHeaders: Record<string, string>;
  controlPlaneEndpoints: Record<string, string>;
  installAckMode: InstallAckMode;
  supportedEvents: SupportedEventPattern[];
  expanded: boolean;
  reviewReason?: string;
}

interface IntegrationAppItem {
  id: string;
  appId: string;
  appName: string;
  provider: string;
  category?: string;
  shortDescription?: string;
  description?: string;
  version?: string;
  iconFileId?: string;
  iconUrl?: string;
  channel?: string;
  publisherAccountId?: string;
  reviewRequired?: boolean;
  reviewStatus?: ReviewStatus;
  controlPlaneDisplayUrl: string;
  controlPlaneBaseUrl: string;
  controlPlaneAuthType: ControlPlaneAuthType;
  controlPlaneSecret?: string;
  controlPlaneHeaders: Record<string, string>;
  controlPlaneEndpoints: Record<string, string>;
  installAckMode: InstallAckMode;
  supportedEvents: SupportedEventPattern[];
  status: IntegrationAppStatus;
  createTime?: number;
  updateTime?: number;
  expanded: boolean;
}

type OptionalEndpointKey = 'update' | 'rotateSecret' | 'uninstall';

type IntegrationAppResponseLike = Partial<IntegrationAppItem> & {
  id?: string;
  appId: string;
  appName: string;
  provider: string;
  category?: string;
  shortDescription?: string;
  description?: string;
  version?: string;
  iconFileId?: string;
  iconUrl?: string;
  channel?: string;
  publisherAccountId?: string;
  reviewRequired?: boolean;
  supportedEvents: SupportedEventPattern[];
  authType?: ControlPlaneAuthType;
  appSecret?: string;
  secret?: string;
  headers?: Record<string, string>;
  installUrl?: string;
  updateUrl?: string;
  rotateSecretUrl?: string;
  uninstallUrl?: string;
  installAckMode?: InstallAckMode;
  status?: IntegrationAppStatus;
  controlPlaneBaseUrl?: string;
  controlPlaneAuthType?: ControlPlaneAuthType;
  controlPlaneSecret?: string;
  controlPlaneHeaders?: Record<string, string>;
  controlPlaneEndpoints?: Record<string, string>;
};

const AUTH_TYPE_OPTIONS: ControlPlaneAuthType[] = ['None', 'HeaderSecret', 'BearerToken', 'CustomHeaders'];
const OPTIONAL_ENDPOINT_KEYS: OptionalEndpointKey[] = ['update', 'rotateSecret', 'uninstall'];
const SUPPORTED_EVENT_DOMAIN_TO_PATTERN: Record<string, SupportedEventPattern> = {
  tenant: 'tenant.*',
  user: 'user.*',
  servicenumber: 'service_number.*',
  contact: 'contact.*',
  visitor: 'visitor.*',
  group: 'group.*',
  addressbook: 'addressbook.*',
  notice: 'notice.*',
  session: 'session.*'
};

const RECOMMENDED_SUPPORTED_EVENTS: SupportedEventPattern[] = [
  'tenant.*',
  'service_number.*',
  'contact.*',
  'user.*',
  'visitor.*',
  'group.*',
  'addressbook.*',
  'notice.*',
  'session.*'
];

const SUPPORTED_EVENT_PATTERN_LABELS: Record<SupportedEventPattern, string> = {
  'tenant.*': 'tenant',
  'service_number.*': 'service-number',
  'contact.*': 'contact',
  'user.*': 'user',
  'visitor.*': 'visitor',
  'group.*': 'group',
  'addressbook.*': 'addressbook',
  'notice.*': 'notice',
  'session.*': 'session'
};

@Component({
  selector: 'app-official-third-party-apps',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzButtonModule,
    NzCardModule,
    NzCheckboxModule,
    NzFormModule,
    NzGridModule,
    NzIconModule,
    NzInputModule,
    NzModalModule,
    NzRadioModule,
    NzSpinModule,
    NzTagModule,
    NzTabsModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './official-third-party-apps.component.html',
  styleUrls: ['./official-third-party-apps.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfficialThirdPartyAppsComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  readonly authTypeOptions = AUTH_TYPE_OPTIONS;
  readonly optionalEndpointOptions = OPTIONAL_ENDPOINT_KEYS;
  readonly recommendedSupportedEvents = RECOMMENDED_SUPPORTED_EVENTS;
  /**
   * 控制是否啟用「我的草稿」Tab。
   * 目前暫時註釋/隱藏草稿 Tab，後續如需開放可切換為 true 並解除 HTML 中的註釋。
   */
  readonly enableDraftsTab = false;
  apps: IntegrationAppItem[] = [];
  reviewApps: AppReviewItem[] = [];
  activeTab = 0;

  get visibleTabs(): OfficialThirdPartyTabType[] {
    return this.enableDraftsTab ? ['connected', 'drafts', 'review'] : ['connected', 'review'];
  }

  get currentTab(): OfficialThirdPartyTabType {
    return this.visibleTabs[this.activeTab] ?? 'connected';
  }
  selectedReview: AppReviewItem | null = null;
  reviewDecision: 'approve' | 'changes' = 'approve';
  reviewReason = '';
  reviewReasonCode: RejectReasonCode = 'Other';
  loading = false;
  modalVisible = false;
  modalMode: 'create' | 'edit' = 'create';
  editingAppId: string | null = null;
  saving = false;
  operatingAppId: string | null = null;
  deleteConfirmVisible = false;
  deleteConfirmInput = '';
  pendingDeleteApp: IntegrationAppItem | null = null;
  customSupportedEventDraft = '';
  iconFile: File | null = null;
  private readonly visibleSecretAppIds = new Set<string>();

  onIconSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.iconFile = file;
    this.appForm.patchValue({ iconFileId: '', iconUrl: URL.createObjectURL(file) });
    this.appForm.controls.iconFileId.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  private readonly trimmedRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
    return String(control.value ?? '').trim() ? null : { required: true };
  };

  private readonly trimmedHttpUrlValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (!value) {
      return { required: true };
    }

    try {
      return new URL(value).protocol === 'https:' ? null : { pattern: true };
    } catch {
      return { pattern: true };
    }
  };

  private readonly iconRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
    const iconUrl = String(control.parent?.get('iconUrl')?.value ?? '').trim();
    return this.iconFile || iconUrl || String(control.value ?? '').trim() ? null : { required: true };
  };

  private readonly supportedEventsValidator = (control: AbstractControl): ValidationErrors | null => {
    return this.parseSupportedEventsText(String(control.value ?? '')).length > 0 ? null : { required: true };
  };

  private readonly keyValueRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
    return this.getObjectSize(this.parseMapText(String(control.value ?? ''))) > 0 ? null : { required: true };
  };

  readonly appForm = this.fb.nonNullable.group({
    appId: ['', [this.trimmedRequiredValidator, Validators.maxLength(100)]],
    appName: ['', [this.trimmedRequiredValidator, Validators.maxLength(100)]],
    provider: ['', [this.trimmedRequiredValidator, Validators.maxLength(100)]],
    category: ['', [this.trimmedRequiredValidator, Validators.maxLength(50)]],
    shortDescription: ['', [this.trimmedRequiredValidator, Validators.maxLength(200)]],
    description: ['', [this.trimmedRequiredValidator, Validators.maxLength(5000)]],
    version: ['', [this.trimmedRequiredValidator, Validators.maxLength(30)]],
    iconFileId: ['', [this.iconRequiredValidator]],
    iconUrl: [''],
    controlPlaneBaseUrl: ['', [this.trimmedRequiredValidator, this.trimmedHttpUrlValidator]],
    controlPlaneAuthType: this.fb.nonNullable.control<ControlPlaneAuthType>('None', Validators.required),
    installAckMode: this.fb.nonNullable.control<InstallAckMode>('Sync', Validators.required),
    controlPlaneSecret: ['', [Validators.maxLength(500)]],
    controlPlaneHeadersText: ['', [Validators.maxLength(2000)]],
    controlPlaneInstallEndpoint: ['', [this.trimmedRequiredValidator, Validators.maxLength(500)]],
    optionalEndpointKeys: this.fb.nonNullable.control<OptionalEndpointKey[]>([]),
    controlPlaneUpdateEndpoint: ['', [Validators.maxLength(500)]],
    controlPlaneRotateSecretEndpoint: ['', [Validators.maxLength(500)]],
    controlPlaneUninstallEndpoint: ['', [Validators.maxLength(500)]],
    supportedEventsText: ['', [this.supportedEventsValidator, Validators.maxLength(2000)]]
  });

  constructor() {
    this.appForm.controls.controlPlaneAuthType.valueChanges.subscribe(authType => {
      this.updateAuthDependentValidators(authType);
    });
    this.appForm.controls.optionalEndpointKeys.valueChanges.subscribe(keys => {
      this.updateOptionalEndpointValidators(keys);
    });
    this.appForm.controls.iconUrl.valueChanges.subscribe(() => {
      this.appForm.controls.iconFileId.updateValueAndValidity({ emitEvent: false });
    });
    this.updateAuthDependentValidators(this.appForm.controls.controlPlaneAuthType.value);
    this.updateOptionalEndpointValidators(this.appForm.controls.optionalEndpointKeys.value);
  }

  ngOnInit(): void {
    this.loadApps();
  }

  get shouldShowSecretField(): boolean {
    const authType = this.appForm.controls.controlPlaneAuthType.value;
    return authType === 'HeaderSecret' || authType === 'BearerToken';
  }

  get shouldShowHeadersField(): boolean {
    return this.appForm.controls.controlPlaneAuthType.value === 'CustomHeaders';
  }

  get deleteConfirmIdentifier(): string {
    return this.pendingDeleteApp?.appId ?? '';
  }

  get deleteConfirmIdentifierMatches(): boolean {
    return this.deleteConfirmInput.trim() === this.deleteConfirmIdentifier;
  }

  getAuthTypeLabelI18nKey(authType: ControlPlaneAuthType): string {
    return `official.third-party-app.auth-type.${authType}`;
  }

  get supportedEventsErrorI18nKey(): string {
    return 'official.third-party-app.form.supported-events-required';
  }

  get selectedSupportedEvents(): SupportedEventPattern[] {
    return this.parseSupportedEventsText(this.appForm.controls.supportedEventsText.value);
  }

  getInstallAckModeLabelI18nKey(mode: InstallAckMode): string {
    return `official.third-party-app.install-ack-mode.${mode}`;
  }

  isRecommendedSupportedEventSelected(pattern: SupportedEventPattern): boolean {
    return this.selectedSupportedEvents.includes(pattern);
  }

  getSupportedEventLabel(pattern: SupportedEventPattern): string {
    const key = SUPPORTED_EVENT_PATTERN_LABELS[pattern];
    return key ? this.i18n.fanyi(`official.third-party-app.supported-event.${key}`) : pattern;
  }

  get controlPlaneEndpointsErrorI18nKey(): string {
    return 'official.third-party-app.form.control-plane-endpoints-required';
  }

  getObjectSize(value: Record<string, string>): number {
    return Object.keys(value).length;
  }

  isSecretVisible(appId: string): boolean {
    return this.visibleSecretAppIds.has(appId);
  }

  toggleSecretVisibility(appId: string): void {
    if (this.visibleSecretAppIds.has(appId)) {
      this.visibleSecretAppIds.delete(appId);
    } else {
      this.visibleSecretAppIds.add(appId);
    }
    this.cdr.markForCheck();
  }

  getDisplaySecret(app: IntegrationAppItem): string {
    const secret = app.controlPlaneSecret ?? '';
    if (!secret || this.isSecretVisible(app.id)) {
      return secret;
    }
    return this.maskSecret(secret);
  }

  copySecret(app: IntegrationAppItem): void {
    const secret = app.controlPlaneSecret?.trim() ?? '';
    if (!secret) {
      this.message.error(this.i18n.fanyi('common.copy-failed'));
      return;
    }

    const nav = navigator as Navigator & {
      clipboard?: {
        writeText(text: string): Promise<void>;
      };
    };

    if (nav.clipboard?.writeText) {
      nav.clipboard
        .writeText(secret)
        .then(() => this.message.success(this.i18n.fanyi('common.copy-success')))
        .catch(() => this.copySecretWithFallback(secret));
      return;
    }

    this.copySecretWithFallback(secret);
  }

  trackByApp(_: number, item: IntegrationAppItem): string {
    return item.id;
  }

  openCreateModal(): void {
    this.modalMode = 'create';
    this.editingAppId = null;
    this.resetForm();
    this.modalVisible = true;
  }

  openEditModal(app: IntegrationAppItem): void {
    const optionalEndpointKeys = OPTIONAL_ENDPOINT_KEYS.filter(key => Boolean(app.controlPlaneEndpoints[key]));
    this.modalMode = 'edit';
    this.editingAppId = app.id;
    this.appForm.patchValue({
      appId: app.appId,
      appName: app.appName,
      provider: app.provider,
      category: app.category ?? '',
      shortDescription: app.shortDescription ?? '',
      description: app.description ?? '',
      version: app.version ?? '',
      iconFileId: app.iconFileId ?? '',
      iconUrl: app.iconUrl ?? '',
      controlPlaneBaseUrl: app.controlPlaneBaseUrl,
      controlPlaneAuthType: app.controlPlaneAuthType,
      installAckMode: app.installAckMode,
      controlPlaneSecret: app.controlPlaneSecret ?? '',
      controlPlaneHeadersText: this.stringifyMap(app.controlPlaneHeaders),
      controlPlaneInstallEndpoint: app.controlPlaneEndpoints['install'] ?? '',
      optionalEndpointKeys,
      controlPlaneUpdateEndpoint: app.controlPlaneEndpoints['update'] ?? '',
      controlPlaneRotateSecretEndpoint: app.controlPlaneEndpoints['rotateSecret'] ?? '',
      controlPlaneUninstallEndpoint: app.controlPlaneEndpoints['uninstall'] ?? '',
      supportedEventsText: app.supportedEvents.join('\n')
    });
    this.appForm.controls.iconFileId.updateValueAndValidity();
    this.modalVisible = true;
  }

  closeModal(): void {
    this.modalVisible = false;
    this.customSupportedEventDraft = '';
  }

  saveApp(): void {
    this.markFormDirty();
    if (this.appForm.controls.iconFileId.hasError('required')) {
      this.message.error(this.i18n.fanyi('official.third-party-app.form.icon-required'));
      return;
    }
    if (this.appForm.invalid || this.saving) {
      return;
    }

    const value = this.appForm.getRawValue();
    const payload = {
      appId: value.appId.trim(),
      appName: value.appName.trim(),
      provider: value.provider.trim(),
      category: value.category.trim() || undefined,
      shortDescription: value.shortDescription.trim() || undefined,
      description: value.description.trim() || undefined,
      version: value.version.trim() || undefined,
      iconFileId: value.iconFileId.trim() || undefined,
      iconUrl: value.iconUrl.trim() || undefined,
      authType: value.controlPlaneAuthType,
      installAckMode: value.installAckMode,
      appSecret: value.controlPlaneSecret.trim() || undefined,
      secret: value.controlPlaneSecret.trim() || undefined,
      headers: this.getOptionalMap(value.controlPlaneHeadersText),
      installUrl: this.buildEndpointUrl(value.controlPlaneBaseUrl, value.controlPlaneInstallEndpoint),
      updateUrl: value.optionalEndpointKeys.includes('update')
        ? this.buildEndpointUrl(value.controlPlaneBaseUrl, value.controlPlaneUpdateEndpoint)
        : undefined,
      rotateSecretUrl: value.optionalEndpointKeys.includes('rotateSecret')
        ? this.buildEndpointUrl(value.controlPlaneBaseUrl, value.controlPlaneRotateSecretEndpoint)
        : undefined,
      uninstallUrl: value.optionalEndpointKeys.includes('uninstall')
        ? this.buildEndpointUrl(value.controlPlaneBaseUrl, value.controlPlaneUninstallEndpoint)
        : undefined,
      supportedEvents: this.parseSupportedEventsText(value.supportedEventsText)
    };

    const request$ = this.http.postFormData(
      this.editingAppId ? ConstantUtil.OPENAPI_INTEGRATION_APP_V1_UPDATE : ConstantUtil.OPENAPI_INTEGRATION_APP_V1_CREATE,
      payload,
      this.iconFile,
      'iconFile'
    );

    this.saving = true;
    request$
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(result => {
        if (!result.success) {
          if (result.msg) {
            this.message.error(result.msg);
          }
          return;
        }

        const previousItem = this.apps.find(item => item.id === this.editingAppId);
        const fallbackStatus: IntegrationAppStatus = previousItem?.status ?? 'Draft';
        const nextItem = this.toViewModel({ status: fallbackStatus, ...this.mergeResponseData(result.data, payload) }, true);
        if (this.editingAppId) {
          this.apps = this.apps.map(item => (item.id === this.editingAppId ? nextItem : item));
          this.message.success(this.i18n.fanyi('official.third-party-app.update-success'));
        } else {
          this.apps = [nextItem, ...this.apps.map(item => ({ ...item, expanded: false }))];
          this.message.success(this.i18n.fanyi('official.third-party-app.create-success'));
        }

        this.modalVisible = false;
      });
  }

  toggleExpanded(appId: string): void {
    this.apps = this.apps.map(item => (item.id === appId ? { ...item, expanded: !item.expanded } : item));
  }

  toggleRecommendedSupportedEvent(pattern: SupportedEventPattern, checked: boolean): void {
    const current = this.selectedSupportedEvents;
    const next = checked ? [...current, pattern] : current.filter(item => item !== pattern);
    this.setSupportedEvents(next);
  }

  addCustomSupportedEvent(): void {
    const normalized = this.normalizeSupportedEvent(this.customSupportedEventDraft);
    this.customSupportedEventDraft = '';
    if (!normalized) {
      return;
    }
    this.setSupportedEvents([...this.selectedSupportedEvents, normalized]);
  }

  removeSupportedEvent(pattern: SupportedEventPattern): void {
    this.setSupportedEvents(this.selectedSupportedEvents.filter(item => item !== pattern));
  }

  enableApp(app: IntegrationAppItem): void {
    if (this.operatingAppId) {
      return;
    }
    this.changeAppStatus(ConstantUtil.OPENAPI_INTEGRATION_APP_V1_ENABLE, app, 'official.third-party-app.enable-success');
  }

  disableApp(app: IntegrationAppItem): void {
    if (this.operatingAppId) {
      return;
    }
    this.changeAppStatus(ConstantUtil.OPENAPI_INTEGRATION_APP_V1_DISABLE, app, 'official.third-party-app.disable-success');
  }

  refreshApps(): void {
    if (this.loading) {
      return;
    }
    this.loadApps();
  }

  submitDraft(app: IntegrationAppItem): void {
    if (this.operatingAppId || (app.status !== 'Draft' && app.status !== 'Rejected')) {
      return;
    }

    this.modal.confirm({
      nzTitle: this.i18n.fanyi('official.third-party-app.draft.submit-title'),
      nzContent: this.i18n.fanyi('official.third-party-app.draft.submit-content', { name: app.appName }),
      nzOkText: this.i18n.fanyi('official.third-party-app.draft.submit'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () => this.confirmSubmitDraft(app)
    });
  }

  toggleReviewExpanded(item: AppReviewItem): void {
    this.reviewApps = this.reviewApps.map(current => (current.id === item.id ? { ...current, expanded: !current.expanded } : current));
  }

  openReview(item: AppReviewItem): void {
    this.selectedReview = item;
    this.reviewDecision = 'approve';
    this.reviewReason = item.reviewReason ?? '';
    this.reviewReasonCode = 'Other';
  }

  closeReview(): void {
    this.selectedReview = null;
    this.reviewReason = '';
  }

  submitReview(): boolean {
    if (!this.selectedReview || (this.reviewDecision === 'changes' && !this.reviewReason.trim())) {
      if (this.reviewDecision === 'changes') {
        this.message.warning(this.i18n.fanyi('official.third-party-app.review.reason-required'));
      }
      return false;
    }
    const selectedReview = this.selectedReview;
    const payload =
      this.reviewDecision === 'approve'
        ? { appId: selectedReview.appId, decision: 'Approve' }
        : {
            appId: selectedReview.appId,
            decision: 'Reject',
            rejectReasonCode: this.reviewReasonCode,
            rejectDescription: this.reviewReason.trim()
          };
    this.http.post(ConstantUtil.OPENAPI_INTEGRATION_APP_V1_REVIEW, payload).subscribe(result => {
      if (!result.success) {
        if (result.msg) this.message.error(result.msg);
        return;
      }
      this.closeReview();
      this.loadApps();
    });
    return true;
  }

  get pendingReviewCount(): number {
    return this.reviewApps.length;
  }

  onTabChanged(index: number): void {
    this.activeTab = index;
    this.loadApps();
  }

  get filteredReviewApps(): AppReviewItem[] {
    return this.reviewApps;
  }

  getReviewChannelLabelI18nKey(channel: string): string {
    return `official.third-party-app.channel.${channel || 'Unknown'}`;
  }

  getCategoryLabel(category?: string): string {
    if (category === 'CRM' || category === 'Productivity') {
      return this.i18n.fanyi(`official.third-party-app.category.${category}`);
    }
    return category || '-';
  }

  deleteApp(app: IntegrationAppItem): void {
    if (!app || this.operatingAppId) {
      return;
    }

    if (this.currentTab === 'drafts' || app.status === 'Draft' || app.status === 'Rejected') {
      this.modal.confirm({
        nzTitle: this.i18n.fanyi('official.third-party-app.draft.delete-confirm-title'),
        nzContent: this.i18n.fanyi('official.third-party-app.draft.delete-confirm-content', {
          name: app.appName,
          appId: app.appId
        }),
        nzOkText: this.i18n.fanyi('common.confirm'),
        nzOkDanger: true,
        nzCancelText: this.i18n.fanyi('common.cancel'),
        nzOnOk: () => this.executeDeleteApp(app, true)
      });
      return;
    }

    this.modal.confirm({
      nzTitle: this.i18n.fanyi('official.third-party-app.delete-confirm.first-title'),
      nzContent: this.i18n.fanyi('official.third-party-app.delete-confirm.first-content', { name: app.appName, appId: app.appId }),
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () => this.openDeleteNameConfirm(app)
    });
  }

  closeDeleteConfirm(): void {
    this.deleteConfirmVisible = false;
    this.deleteConfirmInput = '';
    this.pendingDeleteApp = null;
    this.cdr.markForCheck();
  }

  onDeleteConfirmInput(value: string): void {
    this.deleteConfirmInput = value;
  }

  confirmDeleteApp(): void {
    if (!this.pendingDeleteApp || this.operatingAppId || !this.deleteConfirmIdentifierMatches) {
      return;
    }

    this.executeDeleteApp(this.pendingDeleteApp, false);
  }

  private executeDeleteApp(app: IntegrationAppItem, isDraft: boolean = false): void {
    this.operatingAppId = app.id;
    this.http
      .post(ConstantUtil.OPENAPI_INTEGRATION_APP_V1_DELETE, { appId: app.appId })
      .pipe(
        finalize(() => {
          this.operatingAppId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe(result => {
        if (!result.success) {
          if (result.msg) {
            this.message.error(result.msg);
          }
          return;
        }

        this.apps = this.apps.filter(item => item.id !== app.id);
        this.closeDeleteConfirm();
        const successMessageKey = isDraft ? 'official.third-party-app.draft.delete-success' : 'official.third-party-app.delete-success';
        this.message.success(this.i18n.fanyi(successMessageKey));
      });
  }

  private openDeleteNameConfirm(app: IntegrationAppItem): void {
    this.pendingDeleteApp = app;
    this.deleteConfirmInput = '';
    this.deleteConfirmVisible = true;
    this.cdr.markForCheck();
  }

  private maskSecret(secret: string): string {
    if (!secret) {
      return '';
    }
    if (secret.length <= 8) {
      return '*'.repeat(8);
    }
    return `${secret.slice(0, 4)}${'*'.repeat(Math.max(4, secret.length - 8))}${secret.slice(-4)}`;
  }

  private copySecretWithFallback(secret: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = secret;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      const ok = document.execCommand('copy');
      if (ok) {
        this.message.success(this.i18n.fanyi('common.copy-success'));
      } else {
        this.message.error(this.i18n.fanyi('common.copy-failed'));
      }
    } catch {
      this.message.error(this.i18n.fanyi('common.copy-failed'));
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private loadApps(): void {
    this.loading = true;
    const statusByTab: Record<OfficialThirdPartyTabType, 'Active' | 'Draft' | 'InReview'> = {
      connected: 'Active',
      drafts: 'Draft',
      review: 'InReview'
    };
    this.http
      .post(ConstantUtil.OPENAPI_INTEGRATION_APP_V1_LIST, { status: statusByTab[this.currentTab] ?? 'Active' })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(result => {
        if (!result.success) {
          this.apps = [];
          this.reviewApps = [];
          return;
        }

        const items = Array.isArray((result.data as { items?: unknown[] } | undefined)?.items)
          ? ((result.data as { items: IntegrationAppResponseLike[] }).items ?? [])
          : Array.isArray(result.data)
            ? (result.data as IntegrationAppResponseLike[])
            : [];
        const listItems =
          this.currentTab === 'connected'
            ? items.filter(item => !item.status || item.status === 'Active' || item.status === 'Disabled')
            : this.currentTab === 'drafts'
              ? items.filter(item => item.status === 'Draft' || item.status === 'Rejected')
              : [];
        const reviewItems = this.currentTab === 'review' ? items.filter(item => item.status === 'InReview') : [];
        this.apps = listItems.map((item, index) => this.toViewModel(item, index === 0));
        this.reviewApps = reviewItems.map(item => this.toReviewViewModel(item));
      });
  }

  private confirmSubmitDraft(app: IntegrationAppItem): void {
    this.operatingAppId = app.id;
    this.http
      .post(ConstantUtil.OPENAPI_INTEGRATION_APP_V1_SUBMIT, { appId: app.appId })
      .pipe(
        finalize(() => {
          this.operatingAppId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe(result => {
        if (!result.success) {
          if (result.msg) {
            this.message.error(result.msg);
          }
          return;
        }

        this.apps = this.apps.filter(item => item.id !== app.id);
        this.message.success(this.i18n.fanyi('official.third-party-app.draft.submit-success'));
      });
  }

  private toReviewViewModel(source: IntegrationAppResponseLike): AppReviewItem {
    const reviewStatus = this.toReviewStatus(source.status);
    return {
      id: source.id || source.appId,
      appName: source.appName,
      appId: source.appId,
      provider: source.provider,
      channel: source.channel || 'Unknown',
      version: source.version || '-',
      submittedAt: source.createTime ? new Date(source.createTime).toLocaleString() : '-',
      status: reviewStatus,
      description: source.description || source.shortDescription || '-',
      category: source.category,
      shortDescription: source.shortDescription,
      iconUrl: source.iconUrl,
      controlPlaneBaseUrl: source.controlPlaneBaseUrl || this.getDisplayUrl(source, {}),
      controlPlaneAuthType: source.controlPlaneAuthType ?? source.authType ?? 'None',
      controlPlaneSecret: source.controlPlaneSecret ?? source.appSecret ?? source.secret,
      controlPlaneHeaders: source.controlPlaneHeaders ?? source.headers ?? {},
      controlPlaneEndpoints: source.controlPlaneEndpoints ?? this.getEndpointMap(source),
      installAckMode: source.installAckMode ?? 'Sync',
      supportedEvents: (source.supportedEvents ?? [])
        .map(item => this.normalizeSupportedEvent(item))
        .filter((item): item is SupportedEventPattern => Boolean(item)),
      expanded: false
    };
  }

  private toReviewStatus(value: unknown): ReviewStatus {
    return value === 'Draft' || value === 'InReview' || value === 'Rejected' ? value : 'InReview';
  }

  private resetForm(): void {
    this.iconFile = null;
    this.appForm.reset({
      appId: '',
      appName: '',
      provider: '',
      category: '',
      shortDescription: '',
      description: '',
      version: '',
      iconFileId: '',
      iconUrl: '',
      controlPlaneBaseUrl: '',
      controlPlaneAuthType: 'None',
      installAckMode: 'Sync',
      controlPlaneSecret: '',
      controlPlaneHeadersText: '',
      controlPlaneInstallEndpoint: '',
      optionalEndpointKeys: [],
      controlPlaneUpdateEndpoint: '',
      controlPlaneRotateSecretEndpoint: '',
      controlPlaneUninstallEndpoint: '',
      supportedEventsText: RECOMMENDED_SUPPORTED_EVENTS.join('\n')
    });
    this.appForm.controls.iconFileId.updateValueAndValidity();
    this.customSupportedEventDraft = '';
  }

  private markFormDirty(): void {
    Object.values(this.appForm.controls).forEach(control => {
      control.markAsDirty();
      control.updateValueAndValidity();
    });
  }

  private updateAuthDependentValidators(authType: ControlPlaneAuthType): void {
    const secretControl = this.appForm.controls.controlPlaneSecret;
    const headersControl = this.appForm.controls.controlPlaneHeadersText;

    secretControl.clearValidators();
    headersControl.clearValidators();

    if (authType === 'HeaderSecret' || authType === 'BearerToken') {
      secretControl.setValidators([this.trimmedRequiredValidator, Validators.maxLength(500)]);
      headersControl.setValidators([Validators.maxLength(2000)]);
    } else if (authType === 'CustomHeaders') {
      secretControl.setValidators([Validators.maxLength(500)]);
      headersControl.setValidators([this.keyValueRequiredValidator, Validators.maxLength(2000)]);
    } else {
      secretControl.setValidators([Validators.maxLength(500)]);
      headersControl.setValidators([Validators.maxLength(2000)]);
    }

    secretControl.updateValueAndValidity({ emitEvent: false });
    headersControl.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  private updateOptionalEndpointValidators(keys: OptionalEndpointKey[]): void {
    const controlMap: Record<OptionalEndpointKey, AbstractControl> = {
      update: this.appForm.controls.controlPlaneUpdateEndpoint,
      rotateSecret: this.appForm.controls.controlPlaneRotateSecretEndpoint,
      uninstall: this.appForm.controls.controlPlaneUninstallEndpoint
    };

    OPTIONAL_ENDPOINT_KEYS.forEach(key => {
      controlMap[key].setValidators(
        keys.includes(key) ? [this.trimmedRequiredValidator, Validators.maxLength(500)] : [Validators.maxLength(500)]
      );
      controlMap[key].updateValueAndValidity({ emitEvent: false });
    });

    this.cdr.markForCheck();
  }

  private parseMapText(value: string): Record<string, string> {
    return value
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)
      .reduce<Record<string, string>>((acc, line) => {
        const separatorIndex = line.indexOf(':');
        if (separatorIndex <= 0 || /^https?:\/\//i.test(line)) {
          acc[line] = line;
          return acc;
        }

        const key = line.slice(0, separatorIndex).trim();
        const mapValue = line.slice(separatorIndex + 1).trim();
        if (!key && mapValue) {
          acc[mapValue] = mapValue;
          return acc;
        }

        if (key && mapValue) {
          acc[key] = mapValue;
        }
        return acc;
      }, {});
  }

  private stringifyMap(value: Record<string, string>): string {
    return Object.entries(value)
      .map(([key, mapValue]) => `${key}: ${mapValue}`)
      .join('\n');
  }

  isOptionalEndpointSelected(key: OptionalEndpointKey): boolean {
    return this.appForm.controls.optionalEndpointKeys.value.includes(key);
  }

  toggleOptionalEndpoint(key: OptionalEndpointKey, checked: boolean): void {
    const current = this.appForm.controls.optionalEndpointKeys.value;
    const next = checked ? Array.from(new Set([...current, key])) : current.filter(item => item !== key);
    this.appForm.controls.optionalEndpointKeys.setValue(next);

    const controlMap = {
      update: this.appForm.controls.controlPlaneUpdateEndpoint,
      rotateSecret: this.appForm.controls.controlPlaneRotateSecretEndpoint,
      uninstall: this.appForm.controls.controlPlaneUninstallEndpoint
    };

    if (!checked) {
      controlMap[key].setValue('');
    }
  }

  getEndpointLabelI18nKey(key: 'install' | OptionalEndpointKey): string {
    return `official.third-party-app.endpoint.${key}`;
  }

  isOptionalEndpointRequired(key: OptionalEndpointKey): boolean {
    return this.isOptionalEndpointSelected(key);
  }

  private getOptionalMap(value: string): Record<string, string> | undefined {
    const parsed = this.parseMapText(value);
    return Object.keys(parsed).length ? parsed : undefined;
  }

  private buildEndpointUrl(baseUrl: string, endpoint: string): string {
    const normalizedBaseUrl = baseUrl.trim().replace(/\/+$/, '');
    const normalizedEndpoint = endpoint.trim();
    if (/^https?:\/\//i.test(normalizedEndpoint)) {
      return normalizedEndpoint;
    }
    const safeEndpoint = normalizedEndpoint.startsWith('/') ? normalizedEndpoint : `/${normalizedEndpoint}`;
    return `${normalizedBaseUrl}${safeEndpoint}`;
  }

  private changeAppStatus(endpoint: string, app: IntegrationAppItem, successI18nKey: string): void {
    this.operatingAppId = app.id;
    this.http
      .post(endpoint, { appId: app.appId })
      .pipe(
        finalize(() => {
          this.operatingAppId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe(result => {
        if (!result.success) {
          if (result.msg) {
            this.message.error(result.msg);
          }
          return;
        }

        const nextItem = this.toViewModel(result.data ?? { ...app }, app.expanded);
        this.apps = this.apps.map(item => (item.id === app.id ? nextItem : item));
        this.message.success(this.i18n.fanyi(successI18nKey));
      });
  }

  private toViewModel(source: IntegrationAppResponseLike, expanded: boolean): IntegrationAppItem {
    const endpointMap = this.getEndpointMap(source);
    const normalizedBaseUrl = this.getBaseUrl(source, endpointMap);
    const normalizedEndpoints = this.normalizeEndpointValues(endpointMap, normalizedBaseUrl);

    return {
      id: source.id || source.appId,
      appId: source.appId,
      appName: source.appName,
      provider: source.provider,
      category: source.category,
      shortDescription: source.shortDescription,
      description: source.description,
      version: source.version,
      iconFileId: source.iconFileId,
      iconUrl: source.iconUrl,
      channel: source.channel,
      publisherAccountId: source.publisherAccountId,
      reviewRequired: source.reviewRequired,
      controlPlaneDisplayUrl: this.getDisplayUrl(source, endpointMap),
      controlPlaneBaseUrl: normalizedBaseUrl,
      controlPlaneAuthType: source.controlPlaneAuthType ?? source.authType ?? 'None',
      installAckMode: source.installAckMode ?? 'Sync',
      controlPlaneSecret: source.controlPlaneSecret ?? source.appSecret ?? source.secret,
      controlPlaneHeaders: { ...(source.controlPlaneHeaders ?? source.headers ?? {}) },
      controlPlaneEndpoints: normalizedEndpoints,
      supportedEvents: this.normalizeSupportedEvents(source.supportedEvents),
      status: source.status ?? 'Active',
      createTime: source.createTime,
      updateTime: source.updateTime,
      expanded
    };
  }

  private getEndpointMap(source: IntegrationAppResponseLike): Record<string, string> {
    if (source.controlPlaneEndpoints && Object.keys(source.controlPlaneEndpoints).length > 0) {
      return { ...source.controlPlaneEndpoints };
    }

    const entries: Array<[string, string | undefined]> = [
      ['install', source.installUrl],
      ['update', source.updateUrl],
      ['rotateSecret', source.rotateSecretUrl],
      ['uninstall', source.uninstallUrl]
    ];

    return entries.reduce<Record<string, string>>((acc, [key, value]) => {
      const normalizedValue = String(value ?? '').trim();
      if (normalizedValue) {
        acc[key] = normalizedValue;
      }
      return acc;
    }, {});
  }

  private getBaseUrl(source: IntegrationAppResponseLike, endpoints: Record<string, string>): string {
    if (source.controlPlaneBaseUrl) {
      return source.controlPlaneBaseUrl;
    }

    const urls = Object.values(endpoints).filter(value => /^https?:\/\//i.test(value));
    if (!urls.length) {
      return '';
    }

    const parsedUrls = urls
      .map(value => this.parseAbsoluteUrl(value))
      .filter((value): value is NonNullable<typeof value> => Boolean(value));
    if (!parsedUrls.length) {
      return urls[0];
    }

    const origin = parsedUrls[0].origin;
    if (parsedUrls.some(item => item.origin.toLowerCase() !== origin.toLowerCase())) {
      return origin;
    }

    const segmentLists = parsedUrls.map(item => item.pathname.split('/').filter(Boolean).slice(0, -1));
    const commonSegments = segmentLists[0]?.filter((_, index) => segmentLists.every(list => list[index] === segmentLists[0][index])) ?? [];
    const basePath = commonSegments.length ? `/${commonSegments.join('/')}` : '';
    return `${origin}${basePath}`;
  }

  private getDisplayUrl(source: IntegrationAppResponseLike, endpoints: Record<string, string>): string {
    const explicitBaseUrl = String(source.controlPlaneBaseUrl ?? '').trim();
    if (explicitBaseUrl) {
      return explicitBaseUrl;
    }

    const rawInstallUrl = String(source.installUrl ?? '').trim();
    if (rawInstallUrl) {
      return rawInstallUrl;
    }

    const firstAbsoluteEndpoint = Object.values(endpoints).find(value => /^https?:\/\//i.test(value.trim()));
    return String(firstAbsoluteEndpoint ?? '').trim();
  }

  private normalizeEndpointValues(endpoints: Record<string, string>, baseUrl: string): Record<string, string> {
    if (!baseUrl) {
      return { ...endpoints };
    }

    return Object.entries(endpoints).reduce<Record<string, string>>((acc, [key, value]) => {
      const normalizedValue = value.trim();
      if (!/^https?:\/\//i.test(normalizedValue)) {
        acc[key] = normalizedValue;
        return acc;
      }

      const parsedBaseUrl = this.parseAbsoluteUrl(baseUrl);
      const parsedValue = this.parseAbsoluteUrl(normalizedValue);
      const normalizedBase = baseUrl.replace(/\/$/, '');
      if (
        parsedBaseUrl &&
        parsedValue &&
        parsedBaseUrl.origin.toLowerCase() === parsedValue.origin.toLowerCase() &&
        normalizedValue.startsWith(normalizedBase)
      ) {
        const nextValue = normalizedValue.slice(normalizedBase.length) || '/';
        acc[key] = nextValue.startsWith('/') ? nextValue : `/${nextValue}`;
        return acc;
      }

      acc[key] = normalizedValue;
      return acc;
    }, {});
  }

  private parseAbsoluteUrl(value: string): { origin: string; pathname: string } | null {
    const match = value.trim().match(/^(https?:\/\/[^/?#]+)(\/[^?#]*)?(?:[?#].*)?$/i);
    if (!match) {
      return null;
    }

    return {
      origin: match[1],
      pathname: match[2] || '/'
    };
  }

  private mergeResponseData(
    responseData: unknown,
    payload: {
      appId: string;
      appName: string;
      provider: string;
      category?: string;
      shortDescription?: string;
      description?: string;
      version?: string;
      iconFileId?: string;
      iconUrl?: string;
      channel?: string;
      authType: ControlPlaneAuthType;
      installAckMode: InstallAckMode;
      appSecret?: string;
      secret?: string;
      headers?: Record<string, string>;
      installUrl: string;
      updateUrl?: string;
      rotateSecretUrl?: string;
      uninstallUrl?: string;
      supportedEvents: SupportedEventPattern[];
    }
  ): IntegrationAppResponseLike {
    const response =
      responseData && typeof responseData === 'object' ? (responseData as IntegrationAppResponseLike) : ({} as IntegrationAppResponseLike);
    const normalizedSupportedEvents = this.normalizeSupportedEvents(response.supportedEvents);

    return {
      ...response,
      appId: response.appId ?? payload.appId,
      appName: response.appName ?? payload.appName,
      provider: response.provider ?? payload.provider,
      category: response.category ?? payload.category,
      shortDescription: response.shortDescription ?? payload.shortDescription,
      description: response.description ?? payload.description,
      version: response.version ?? payload.version,
      iconFileId: response.iconFileId ?? payload.iconFileId,
      iconUrl: response.iconUrl ?? payload.iconUrl,
      channel: response.channel ?? payload.channel,
      publisherAccountId: response.publisherAccountId,
      reviewRequired: response.reviewRequired,
      authType: response.authType ?? payload.authType,
      installAckMode: response.installAckMode ?? payload.installAckMode,
      appSecret: response.appSecret ?? payload.appSecret,
      secret: response.appSecret ?? response.secret ?? payload.appSecret ?? payload.secret,
      headers: response.headers ?? payload.headers,
      installUrl: response.installUrl ?? payload.installUrl,
      updateUrl: response.updateUrl ?? payload.updateUrl,
      rotateSecretUrl: response.rotateSecretUrl ?? payload.rotateSecretUrl,
      uninstallUrl: response.uninstallUrl ?? payload.uninstallUrl,
      supportedEvents: normalizedSupportedEvents.length ? normalizedSupportedEvents : payload.supportedEvents
    };
  }

  private parseSupportedEventsText(value: string): SupportedEventPattern[] {
    return this.normalizeSupportedEvents(
      value
        .split(/\r?\n|,/)
        .map(item => item.trim())
        .filter(Boolean)
    );
  }

  private normalizeSupportedEvents(values: Array<string | SupportedEventPattern> | undefined): SupportedEventPattern[] {
    if (!Array.isArray(values)) {
      return [];
    }

    const normalized = values
      .map(value => this.normalizeSupportedEvent(value))
      .filter((value): value is SupportedEventPattern => Boolean(value));

    return Array.from(new Set(normalized));
  }

  private normalizeSupportedEvent(value: string | SupportedEventPattern): SupportedEventPattern | null {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) {
      return null;
    }

    if (rawValue.includes('*')) {
      return rawValue.toLowerCase();
    }

    const snakeCaseValue = rawValue
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[-\s]+/g, '_')
      .toLowerCase();

    if (snakeCaseValue.includes('.')) {
      const [rawDomain, rawEvent] = snakeCaseValue.split('.', 2);
      const normalizedDomain = rawDomain.replace(/_/g, '');
      const domainPattern = SUPPORTED_EVENT_DOMAIN_TO_PATTERN[normalizedDomain];
      if (domainPattern) {
        const domainPrefix = domainPattern.replace(/\.\*$/, '');
        return rawEvent ? `${domainPrefix}.${rawEvent}` : domainPattern;
      }
      return snakeCaseValue;
    }

    const normalizedDomainKey = snakeCaseValue.replace(/_/g, '');
    if (SUPPORTED_EVENT_DOMAIN_TO_PATTERN[normalizedDomainKey]) {
      return SUPPORTED_EVENT_DOMAIN_TO_PATTERN[normalizedDomainKey];
    }

    return snakeCaseValue;
  }

  private setSupportedEvents(values: SupportedEventPattern[]): void {
    this.appForm.controls.supportedEventsText.setValue(this.normalizeSupportedEvents(values).join('\n'));
    this.appForm.controls.supportedEventsText.markAsDirty();
    this.appForm.controls.supportedEventsText.updateValueAndValidity();
    this.cdr.markForCheck();
  }
}
